import { keepPreviousData, useQuery } from "@tanstack/react-query";
import fetchToBackend, { buildQuery } from "@/utils/fetchToBackend";
import { tdmFiltersToParams, type TdmFilterState } from "@/hooks/useTdmFilters";
import type { TdmContractingRow, TdmDrill, TdmStats } from "@/types/tdm";

export function useTdmStats(filters: TdmFilterState, enabled = true) {
  const params = tdmFiltersToParams(filters);
  return useQuery({
    queryKey: ["tdm", "stats", params],
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await fetchToBackend<{ data: TdmStats }>(
        `/tdm/stats${buildQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useTdmContractings(
  filters: TdmFilterState,
  page: number,
  size: number,
  enabled = true,
) {
  const params = { ...tdmFiltersToParams(filters), page, size };
  return useQuery({
    queryKey: ["tdm", "contractings", params],
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await fetchToBackend<{
        data: { rows: TdmContractingRow[]; total: number };
      }>(`/tdm/contractings${buildQuery(params)}`);
      return res.data;
    },
  });
}

/**
 * Las 10 obras que mueven el número que se clicó. El corte va encima de los
 * filtros vigentes; sin `drill` la query queda desactivada.
 */
export function useTdmDrilldown(filters: TdmFilterState, drill: TdmDrill | null) {
  const params = {
    ...tdmFiltersToParams(filters),
    departmentNames: drill?.departmentNames,
    quarter: drill?.quarter,
    orderQtyCategory: drill?.qtyCategory,
    // El corte manda sobre el filtro de la barra: es el número que se clicó.
    categories: drill?.categories ?? tdmFiltersToParams(filters).categories,
    sectors: drill?.sectors ?? tdmFiltersToParams(filters).sectors,
    page: 0,
    size: 10,
  };
  return useQuery({
    queryKey: ["tdm", "drilldown", params],
    enabled: !!drill,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await fetchToBackend<{
        data: { rows: TdmContractingRow[]; total: number };
      }>(`/tdm/contractings${buildQuery(params)}`);
      return res.data;
    },
  });
}
